import { Injectable } from '@angular/core';
import { Observable, Subscription, forkJoin, interval, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap, delay } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AppConfigService, OpCo } from '@eucoms/common-components';
import { ProductInfo, ProductDefinition, SummaryMappingRule } from '@eucoms/customer-touchpoints-tracker';
import { AccountTouchPointSummary } from '@eucoms/customer-touchpoints-tracker';
import { JourneyDefinition } from '@eucoms/customer-touchpoints-tracker';

// Define a simple interface for filter parameters
export interface TouchPointFilterParams {
  [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
}
interface CacheEntry<T> {
  timestamp: number;
  data: T;
  ttl: number;
}
@Injectable({ providedIn: 'root' })
export class ApiService {

  private apiUrl: string;
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 60 * 1000; // TTL of 1 minute for touchpoints and summaries
  private productConfigTTL = 5 * 60 * 1000; // 5 min for config
  private cleanupSub: Subscription = new Subscription();
  private storagePrefix = 'ctp_cache:'; // prefix for localStorage keys

  constructor(
    private appConfig: AppConfigService,
    private http: HttpClient) {
      this.apiUrl = this.appConfig.config.custtpApiUrl;
      this.cleanupSub = interval(300 * 1000).subscribe(() => this.cleanupCache());
  }

  private getFromCache<T>(
    key: string,
    request$: Observable<T>,
    forceRefresh = false,
    ttl: number = this.defaultTTL
  ): Observable<T> {
    const now = Date.now();
    const storageKey = this.storagePrefix + key;
    let cached: CacheEntry<T> | undefined = this.cache.get(key);

    // Try localStorage first
    if (!forceRefresh) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          cached = JSON.parse(raw) as CacheEntry<T>;
        }
      } catch { /* ignore parse errors */ }
    }

    if (!forceRefresh && cached && now - cached.timestamp < cached.ttl) {
      return of(cached.data); // valid cached entry
    }

    return request$.pipe(
      tap(res => {
        const entry: CacheEntry<T> = { timestamp: now, data: res, ttl: ttl };
        this.cache.set(key, entry); // maintain in-memory copy
        try { localStorage.setItem(storageKey, JSON.stringify(entry)); } catch { /* quota or unavailable */ }
      })
    );
  }

  ngOnDestroy() {
    this.cleanupSub.unsubscribe();
  }

  clearCache(): void {
    // Clear localStorage entries with prefix
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(this.storagePrefix)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
    this.cache.clear();
  }

  invalidateKey(url: string, body?: any): void {
    const key = body ? url + JSON.stringify(body) : url;
    const storageKey = this.storagePrefix + key;
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    this.cache.delete(key);
  }

  // prevent cache from becoming too large?
  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.ttl) this.cache.delete(key);
    }
    // Also clean expired localStorage entries
    try {
      const expired: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(this.storagePrefix)) continue;
        const raw = localStorage.getItem(k);
        if (!raw) { expired.push(k); continue; }
        try {
          const parsed = JSON.parse(raw) as CacheEntry<any>;
          if (now - parsed.timestamp >= parsed.ttl) expired.push(k);
        } catch { expired.push(k); }
      }
      expired.forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
  }

  // hash to store requests with parameters
  private hashCode(str: string): number {
      let h = 0;
      for (let i = 0; i < str.length; i++) {
          h = 31 * h + str.charCodeAt(i);
          h = h | 0; // Force to 32-bit integer
      }
      return h;
  }

  createTouchpoint(touchpoint: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/touchpoints`, touchpoint,
      { observe: 'response', responseType: 'text' }
    ).pipe(tap(() => { this.invalidateKey(`${this.apiUrl}/touchpoints/filter`);}),
      catchError(this.handleError)
    );
  }

   createTouchpoints(touchpoints: any[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/touchpoints/batch`, touchpoints,
      { observe: 'response' }
    ).pipe(tap(() => { this.invalidateKey(`${this.apiUrl}/touchpoints/filter`);}),
      catchError(this.handleError)
    );
  }

  getTouchpoints(forceRefresh = false): Observable<any> {
    const url = `${this.apiUrl}/touchpoints`;
    return this.getFromCache(url, this.http.get(url), forceRefresh);
  }

  getTouchpointById(id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/touchpoints/${id}`);
  }

  getSummaries(): Observable<any> {
    return this.http.get(`${this.apiUrl}/summaries`);
  }

  getTouchpointsByFilter(filter: TouchPointFilterParams, forceRefresh = false): Observable<any> {
    // Remove null or undefined values from the filter object
    const cleanFilter = Object.entries(filter)
    .filter(([_, value]) => value !== null && value !== undefined)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {} as TouchPointFilterParams);

    const requestBody = cleanFilter;

    const url = `${this.apiUrl}/touchpoints/filter`;
    const key = url + this.hashCode(JSON.stringify(requestBody));

    return this.getFromCache(key, this.http.post<any>(url, requestBody), forceRefresh).pipe(
      catchError(this.handleError)
    );
  }

  getSummaryFromProxy(accountNumber: string, forceRefresh = false): Observable<AccountTouchPointSummary | null> {
    if (!this.appConfig.config.isPreviewUsingProxy) {
      return this.getSummariesByFilter({ accountNumber: accountNumber })
        .pipe(
          map((response: any) => {
            if (response && response.length > 0) {
              return response[0] as AccountTouchPointSummary;
            }
            return null; // Return null if no summaries found
          }),
          catchError(this.handleError)
        );
    }

    let opCoTLA = "unknown";
    switch (this.appConfig.config.opCo) {
      case OpCo.ACE: opCoTLA = "ace"; break;
      case OpCo.BGE: opCoTLA = "bge"; break;
      case OpCo.ComEd: opCoTLA = "com"; break;
      case OpCo.DPL: opCoTLA = "dpl"; break;
      case OpCo.PECO: opCoTLA = "pec"; break;
      case OpCo.PEPCO: opCoTLA = "pep"; break;
      default: opCoTLA = "invalid"; break;
    }

    const url = `${this.appConfig.config.ssipApiUrl}/eu/digital/dss/custtp/${opCoTLA}/summaries/${accountNumber}`;
    return this.http.get<AccountTouchPointSummary>(url).pipe(catchError(this.handleError));
  }

  // Submit feedback for a summary session
  submitSummaryFeedback(accountNumber: string, sessionId: string, response: 'CastUpVote' | 'CastDownVote' | 'CastResetVote', resultCode: string = '', csrId: string): Observable<any> {
    const payload = { accountNumber, sessionId, resultCode, response, csrId };
    return this.http.post(`${this.apiUrl}/summaries/feedback`, payload).pipe(
      catchError(this.handleError)
    );
  }

  getSummariesByFilter(filter: TouchPointFilterParams, forceRefresh = false): Observable<any> {
    // Remove null or undefined values from the filter object
    const cleanFilter = Object.entries(filter)
    .filter(([_, value]) => value !== null && value !== undefined)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {} as TouchPointFilterParams);

    const requestBody = cleanFilter;
    const url = `${this.apiUrl}/summaries/filter`;
    const key = url + this.hashCode(JSON.stringify(requestBody));
    return this.getFromCache(key, this.http.post<any>(url, requestBody), forceRefresh).pipe(
      catchError(this.handleError)
    );
  }

  getSummaryMappingRules(productId: string, forceRefresh = false): Observable<Array<SummaryMappingRule>> {
    const mappingRulesUrl = `${this.apiUrl}/config/summary-mapping-rules/${productId}`;
    return forkJoin({
      productDefinition: this.getProductDefinitionByProductId(productId),
      mappingRules: this.getFromCache(mappingRulesUrl, this.http.get(mappingRulesUrl), forceRefresh, this.productConfigTTL)
    })
    .pipe(
      map((data) => {
        if (!data.productDefinition) throw new Error('Product definition not found');
        if (!data.mappingRules) throw new Error('Mapping rules not found');
        return (data.mappingRules as Array<SummaryMappingRule>);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching summary mapping rules:', error);
        return throwError(() => new Error('Failed to load summary mapping rules'));
      })
    );
  }

  queryAppInsightsLogs(searchQuery: string, opco: string, environment: string): Observable<any> {
    const url = `${this.appConfig.config.ssipApiUrl}/logs?searchQuery=${searchQuery}&opCo=${opco}&environment=${environment}`;
    return this.http.get<any>(url).pipe(catchError(this.handleError));
  }

   // Enable or disable a journey for a product
  setJourneyEnabled(productId: string, journeyName: string, enabled: boolean): Observable<any> {
    return this.http.post(`${this.apiUrl}/config/product/${productId}/enable`, {
      journeyName,
      enabled
    });
  }

  // Basic error handler for HTTP requests
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Backend returned an unsuccessful response code
      errorMessage = `Server returned code ${error.status}, error message is: ${error.message}`;
      // Log more details if available
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage)); // Return an observable error
  }
  // Get Products from config with caching
  getProducts(forceRefresh = false): Observable<Array<ProductInfo>> {
    const url = `${this.apiUrl}/config/product-list`;
    return this.getFromCache(url, this.http.get<any>(url).pipe(
      map(response => response.products)
    ), forceRefresh, this.productConfigTTL);
  }
  // Get Product Definition for a specific Product from config with caching
  getProductDefinitionByProductId(productId: string, forceRefresh = false): Observable<any> {
    const url = `${this.apiUrl}/config/product/${productId}`;
    return this.getFromCache(url, this.http.get(url), forceRefresh, this.productConfigTTL);
  }

  /* ------------------------------------------------------------------
   * Product CRUD Methods
   * ------------------------------------------------------------------ */
  createProduct(product: Partial<ProductDefinition>): Observable<ProductDefinition> {
    const url = `${this.apiUrl}/config/product`;
    return this.http.post<ProductDefinition>(url, product).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/product-list`)),
      catchError(this.handleError)
    );
  }

  updateProduct(productId: string, patch: Partial<ProductDefinition>): Observable<ProductDefinition> {
    const url = `${this.apiUrl}/config/product`;
    return this.http.put<ProductDefinition>(url, patch).pipe(
      tap(() => {
        this.invalidateKey(`${this.apiUrl}/config/product-list`);
        this.invalidateKey(`${this.apiUrl}/config/product/${productId}`);
      }),
      catchError(this.handleError)
    );
  }

  deleteProduct(productId: string): Observable<boolean> {
    const url = `${this.apiUrl}/config/product/${productId}`;
    return this.http.delete(url).pipe(
      map(() => true),
      tap(() => {
        this.invalidateKey(`${this.apiUrl}/config/product-list`);
        this.invalidateKey(`${this.apiUrl}/config/product/${productId}`);
      }),
      catchError(this.handleError)
    );
  }

  private generateMockId(): string {
    return 'product-' + Math.random().toString(36).substring(2, 9);
  }

  /* ------------------------------------------------------------------
   * Journey CRUD Methods
   * ------------------------------------------------------------------ */
  createJourney(productId: string, journey: Partial<JourneyDefinition>): Observable<JourneyDefinition> {
    const url = `${this.apiUrl}/config/product/${productId}/journey`;
    return this.http.post<JourneyDefinition>(url, journey).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/product/${productId}`)),
      tap(() => this.invalidateKey(`${this.apiUrl}/config/summary-mapping-rules/${productId}`)),
      catchError(this.handleError)
    );
  }

  updateJourney(productId: string, patch: Partial<JourneyDefinition>): Observable<JourneyDefinition> {
    const url = `${this.apiUrl}/config/product/${productId}/journey`;
    return this.http.put<JourneyDefinition>(url, patch).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/product/${productId}`)),
      catchError(this.handleError)
    );
  }

  deleteJourney(productId: string, journeyName: string): Observable<boolean> {
    const url = `${this.apiUrl}/config/product/${productId}/journey/${encodeURIComponent(journeyName)}`;
    return this.http.delete(url).pipe(
      map(() => true),
      tap(() => this.invalidateKey(`${this.apiUrl}/config/product/${productId}`)),
      catchError(this.handleError)
    );
  }

  /* ------------------------------------------------------------------
   * Summary Mapping CRUD Methods 
   * ------------------------------------------------------------------ */

  // Create a new mapping rule
  createSummaryMappingRule(productId: string, rule: SummaryMappingRule): Observable<any> {
    return this.http.post(`${this.apiUrl}/config/summary-mapping-rules/${productId}`, rule).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/summary-mapping-rules/${productId}`)),
      catchError(this.handleError)
    );
  }

  // Update an existing mapping rule
  updateSummaryMappingRule(productId: string, rule: SummaryMappingRule): Observable<any> {
    return this.http.put(`${this.apiUrl}/config/summary-mapping-rules/${productId}`, rule).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/summary-mapping-rules/${productId}`)),
      catchError(this.handleError)
    );
  }

  // Delete a mapping rule
  deleteSummaryMappingRule(productId: string, rule: SummaryMappingRule): Observable<any> {
    return this.http.delete(`${this.apiUrl}/config/summary-mapping-rules/${productId}`, { body: rule }).pipe(
      tap(() => this.invalidateKey(`${this.apiUrl}/config/summary-mapping-rules/${productId}`)),
      catchError(this.handleError)
    );
  }

  // Get Products and Definitions from config in one call
  getProductsAndDefinitions(forceRefresh = false): Observable<any[]> {
    const productListUrl = `${this.apiUrl}/config/product-list`;
    return this.getFromCache(productListUrl, this.http.get<any>(productListUrl).pipe(
      map(response => response.products)
    ), forceRefresh, this.productConfigTTL).pipe(
      switchMap((products) => {
        if (!Array.isArray(products) || products.length === 0) {
          return of([]);
        }
        return forkJoin(
          products.map(product => {
            const definitionUrl = `${this.apiUrl}/config/product/${product.id}`;
            return this.getFromCache(definitionUrl, this.http.get<any>(definitionUrl), forceRefresh, this.productConfigTTL).pipe(
              map(definition => ({ ...product, definition })),
              catchError(error => {
                console.error(`Error fetching definition for product ${product.id}:`, error);
                return of({ ...product, definition: null, error: true });
              })
            );
          })
        );
      }),
      catchError(error => {
        console.error('Error fetching product list:', error);
        return of([]);
      })
    );
  }


}
